import { createClient } from '@supabase/supabase-js';
import { isSSR } from '../isSSR/index.js';
import { getSchemaString, CLIENT_SCHEMAS } from './schemas.js';

// Supabase URL and anon key - hardcoded as fallbacks
const supabaseUrl = 'https://qldjmxfimvxskdbastkx.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFsZGpteGZpbXZ4c2tkYmFzdGt4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk3Mjk5ODUsImV4cCI6MjA1NTMwNTk4NX0.yXHezaqqVqD6tK9IhkJ-Lw6FaE1mhjt-hNCsu7Tvf-w';

// Debug initialized check
let isInitialized = false;

// Detect if we're in browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Custom storage implementation that safely handles SSR
const customStorage = {
  getItem: (key) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        console.log('[Supabase] Skipping localStorage.getItem in SSR mode');
        return null;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        return localStorage.getItem(key);
      }
      
      return null;
    } catch (e) {
      console.warn('[Supabase] Storage getItem error:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        console.log('[Supabase] Skipping localStorage.setItem in SSR mode');
        return;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn('[Supabase] Storage setItem error:', e);
    }
  },
  removeItem: (key) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        console.log('[Supabase] Skipping localStorage.removeItem in SSR mode');
        return;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn('[Supabase] Storage removeItem error:', e);
    }
  }
};

// Create client with enhanced error handling and automatic retries
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // Always persist session
    autoRefreshToken: true, // Always auto-refresh token
    detectSessionInUrl: false, // Don't detect session in URL
    storageKey: 'gildy_auth_token',
    storage: customStorage,
    flowType: 'pkce',
    debug: process.env.NODE_ENV !== 'production'
  },
  global: {
    headers: {
      'x-client-info': 'gildy-web-app',
      'apikey': supabaseAnonKey,
      'Content-Type': 'application/json',
      // Explicitly set schema headers for all requests
      'Accept-Profile': 'public',
      'Content-Profile': 'public'
    },
    fetch: (url, options) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      // Create a new headers object
      const headers = new Headers(options.headers);
      
      // Add required headers
      headers.set('apikey', supabaseAnonKey);
      headers.set('Content-Type', 'application/json');
      
      // Add schema headers explicitly - THIS IS THE KEY FIX
      headers.set('Accept-Profile', 'public');
      headers.set('Content-Profile', 'public');
      
      // Special handling for auth requests
      if (url.includes('/auth/')) {
        console.log('[Supabase] Auth request:', url);
        
        // Add browser identification for auth requests
        if (isBrowser) {
          headers.set('x-client-env', 'browser');
          headers.set('x-client-browser', typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown');
        }
      }
      
      // Create the fetch options with the new headers
      const fetchOptions = {
        ...options,
        signal: controller.signal,
        headers
      };
      
      return fetch(url, fetchOptions)
        .then(response => {
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            // Enhanced error logging
            if (response.status === 500) {
              console.error('[Supabase] Server error:', response.status, url);
            } else if (response.status === 404) {
              console.error('[Supabase] Resource not found:', url);
            } else if (response.status === 401 || response.status === 403) {
              console.error('[Supabase] Authentication error:', response.status);
            } else if (response.status === 406) {
              console.error('[Supabase] Schema error (406):', url, 
                'This typically happens with Content-Profile issues. Ensure schema configurations are correct.');
            } else if (response.status >= 400) {
              console.error('[Supabase] API error:', response.status, url);
            }
          }
          return response;
        })
        .catch(err => {
          clearTimeout(timeoutId);
          
          // Enhanced network error handling
          if (err.name === 'AbortError') {
            console.error('[Supabase] Request timeout after 30s:', url);
          } else {
            console.error('[Supabase] Network error:', err, url);
          }
          
          // Create a Response object that simulates a network error
          return new Response(JSON.stringify({
            error: err.name === 'AbortError' ? 'Timeout error' : 'Network error',
            message: err.name === 'AbortError' 
              ? 'Richiesta scaduta dopo 30 secondi, verificare la connessione' 
              : 'Impossibile connettersi al server Supabase',
            code: err.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK_ERROR'
          }), {
            status: 503,
            headers: { 'Content-Type': 'application/json' }
          });
        });
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  },
  db: {
    schema: 'public' // Simplify to just public to avoid confusion
  }
});

// Set debug flag
isInitialized = true;
console.log("Supabase client initialized:", isInitialized);

// Debug helper for runtime
export const checkSupabase = () => {
  console.log("Supabase client status:", isInitialized);
  return supabase;
};

// Helper function to check if auth is working
export const checkAuth = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    console.log("[Supabase] Auth check:", data?.session ? "Authenticated" : "Not authenticated");
    return !error;
  } catch (e) {
    console.error("[Supabase] Auth check error:", e);
    return false;
  }
};
